// =============================================================================
//  Copyright : 2021-2021 semantics GmbH
//  Date      : $Date: 2022-11-26 00:04:01 +0100 (Sat, 26 Nov 2022) $
//  Author    : Andreas Hesse
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 130883 $
//  Purpose   : Analytics and Session Jobs Dialogs
// =============================================================================
// ---Third-party imports
import React from 'react'
import log from 'uix/logging'
import {Button, Item, Segment, Divider} from 'semantic-ui-react'
import {useSelector} from 'uix/hooks'
import {Sel} from 'uix/registries'
import {HelpDialog} from 'uix/help/dialog'
import {sectionGroupsFromSpec, SpecSections, useEditing} from 'uix/job/components'
import styles from 'uix/job/styles.scss'

const filterSpec = (spec, filter) => {
  let res = {}
  for (const key in spec) {
    const vals = filter[key]
    if (vals) {
      res[key] = spec[key].filter(o => vals.includes(o.name))
    }
  }
  return res
}

const SimpleJobDlg = ({open, spec, params, closeDialog, startJob, name, wikiident, filter}) => {
  let i18n = useSelector(Sel.i18n)
  let [edited, setEditValue, resetEdited] = useEditing(params)
  if (!open) {
    return null
  }

  wikiident = wikiident || 'help/job/' + name.toLowerCase()
  let newspec = filterSpec(spec, filter)
  // log.x(newspec)
  let sections = sectionGroupsFromSpec(newspec)
  // log.x(sections)

  let buttons = (
    <>
      <Button
        content={i18n.get('Start')}
        color="green"
        floated="right"
        onClick={() => {
          startJob(name, edited)
          closeDialog()
        }}
      />
      <Button content={i18n.get('Cancel')} color="red" floated="right" onClick={closeDialog} />
    </>
  )

  // setup Options for starting a parametrized job
  let options = (
    <Item.Content className={styles.jobinfoPane} style={{paddingLeft: 0}}>
      {sections.map((group, i) => (
        <div style={{flex: '1', paddingLeft: i > 0 ? '10px' : null}} key={i}>
          <SpecSections
            jobName={name}
            sections={group}
            edited={edited}
            onEdit={setEditValue}
            resetEdited={resetEdited}
          />
        </div>
      ))}
    </Item.Content>
  )

  return (
    <HelpDialog
      name={name}
      wikiident={wikiident}
      buttons={buttons}
      icon="cog"
      closeOnEsc
      header={name}
      onClose={closeDialog}
    >
      <Item className="job">
        <Segment basic>{i18n.span(name + '_hint')}</Segment>
        <Divider />
        <Segment basic>{options}</Segment>
      </Item>
    </HelpDialog>
  )
}

export const AccessStatsJob = {
  dialog: ({open, spec, params, closeDialog, startJob}) => {
    const name = 'AccessStatsJob'
    const filter = {
      '': ['timelimit'],
      filter: ['dateFrom', 'dateUntil', 'mode'],
      multicore: ['cores'],
      job: ['external'],
    }
    return (
      <SimpleJobDlg
        open={open}
        spec={spec}
        params={params}
        closeDialog={closeDialog}
        startJob={startJob}
        name={name}
        filter={filter}
      />
    )
  },
}

export const AnalyzeSessionHistoryJob = {
  dialog: ({open, spec, params, closeDialog, startJob}) => {
    const name = 'AnalyzeSessionHistoryJob'
    const filter = {'': ['compactSessionCount', 'limit'], job: ['external']}
    return (
      <SimpleJobDlg
        open={open}
        spec={spec}
        params={params}
        closeDialog={closeDialog}
        startJob={startJob}
        name={name}
        filter={filter}
      />
    )
  },
}

export const PurgeSessionData = {
  dialog: ({open, spec, params, closeDialog, startJob}) => {
    const name = 'PurgeSessionData'
    const filter = {'': ['remove_localhost', 'shrink'], job: ['external']}
    return (
      <SimpleJobDlg
        open={open}
        spec={spec}
        params={params}
        closeDialog={closeDialog}
        startJob={startJob}
        name={name}
        filter={filter}
      />
    )
  },
}

export const ResolveUseragents = {
  dialog: ({open, spec, params, closeDialog, startJob}) => {
    const name = 'ResolveUseragents'
    const filter = {'': ['resolve_all', 'flushLocal'], job: ['external']}
    return (
      <SimpleJobDlg
        open={open}
        spec={spec}
        params={params}
        closeDialog={closeDialog}
        startJob={startJob}
        name={name}
        filter={filter}
      />
    )
  },
}
